/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { FC, useState } from 'react'

import {
  deleteNotifications,
  markNotificationsAsUnread,
  readNotifications,
} from '@builddocs/data-management'
import { useToast } from '@builddocs/system'
import {
  Box,
  Checkbox,
  Divider,
  ListItem,
  ListItemButton,
  Stack,
} from '@mui/material'
import * as Sentry from '@sentry/react'

import { NotificationBody } from './NotificationBody'
import NotificationTools from './NotificationTools'

import type { Notification } from '@builddocs/data-management'

interface OwnProps {
  notification: Notification
  last: boolean
  handleChangePageNumber: () => void
  onClosePopover: () => void
  refetch: () => void
  onSelect: () => void
  checked: boolean
}

export type NotificationListItemProps = OwnProps

const NotificationItem: FC<NotificationListItemProps> = props => {
  const {
    notification,
    refetch,
    onSelect,
    checked,
    last,
    handleChangePageNumber,
  } = props
  const { notificationTargetId } = notification

  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const initialDividerStyle = {
    position: 'absolute',
    width: '100%',
    transition: theme =>
      theme.transitions.create(['opacity'], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
  }

  const handleReadOrUnRead = (id: string) => {
    setIsLoading(true)
    ;(notification.viewed ? markNotificationsAsUnread : readNotifications)([id])
      .then(() => refetch())
      .catch(error => {
        Sentry.captureException(error)
        toast.error(
          error?.response?.data?.message ??
            'Не удалось отметить как непрочитанное',
        )
      })
      .finally(() => setIsLoading(false))
  }

  const handleDelete = (id: string) => {
    setIsLoading(true)

    handleChangePageNumber()

    deleteNotifications([id])
      .then(() => refetch())
      .catch(error => {
        Sentry.captureException(error)
        toast.error(
          error?.response?.data?.message ?? 'Не удалось удалить уведомление',
        )
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <ListItem
      key={notificationTargetId}
      disablePadding
      className={notification.deleted ? 'deleted' : undefined}
      sx={{
        '&:hover, &:focus': { '& hr': { opacity: 0 } },
        '&.deleted': {
          opacity: 0.5,
          bgcolor: 'action.disabled',
          '& hr': { opacity: 0 },
        },
      }}
    >
      <ListItemButton
        alignItems="flex-start"
        sx={{ p: 1, '&:hover, &:focus': { '& div': { opacity: 1 } } }}
      >
        <Divider sx={{ ...initialDividerStyle, top: 0, left: 0 }} />
        <Stack
          direction="row"
          gap={1}
          justifyContent="space-between"
          width="100%"
        >
          <Checkbox
            checked={checked}
            sx={{
              height: 'fit-content',
              alignSelf: 'center',
            }}
            onChange={onSelect}
          />
          <NotificationBody {...props} />
          <Box sx={{ pt: 1.5 }}>
            <NotificationTools
              disabled={isLoading}
              title={{
                delete: 'Удалить',
                read: `Отметить как ${
                  notification.viewed ? 'непрочитанное' : 'прочитанное'
                }`,
              }}
              onDelete={() => handleDelete(notification.notificationTargetId)}
              onRead={() =>
                handleReadOrUnRead(notification.notificationTargetId)
              }
            />
          </Box>
        </Stack>
        {last && (
          <Divider sx={{ ...initialDividerStyle, bottom: 0, left: 0 }} />
        )}
      </ListItemButton>
    </ListItem>
  )
}

export default NotificationItem
